/**
 * Authentication Service
 * Handles AWS Cognito authentication operations via Amplify v6
 */
import { fetchAuthSession, signIn, signOut, resetPassword, confirmResetPassword, confirmSignIn } from 'aws-amplify/auth';

export class AuthService {
  /**
   * Securely authenticates user via in-app background API call (USER_SRP_AUTH)
   * This completely replaces the old Hosted UI full-page redirect flow.
   * @param username The user's email address or username
   * @param password The user's password
   */
  static async signInWithCredentials(username: string, password: string) {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username,
        password,
        options: {
          authFlowType: 'USER_SRP_AUTH'
        }
      });
      return { isSignedIn, nextStep };
    } catch (error) {
      console.error('Error signing in with credentials:', error);
      throw error;
    }
  }

  /**
   * Resolves the FORCE_CHANGE_PASSWORD challenge for new users logging in with a temporary password
   */
  static async confirmNewPasswordChallenge(challengeResponse: string) {
    try {
      const { isSignedIn } = await confirmSignIn({ challengeResponse });
      return { isSignedIn };
    } catch (error) {
      console.error('Error resolving new password challenge:', error);
      throw error;
    }
  }

  /**
   * Get the current user's authentication session metadata
   */
  static async getSession() {
    try {
      return await fetchAuthSession();
    } catch (error) {
      console.error('Error fetching auth session:', error);
      throw error;
    }
  }

  /**
   * Get the active ID token string required for API Gateway Authorizers
   */
  static async getIdToken(): Promise<string | undefined> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.error('Error getting ID token:', error);
      throw error;
    }
  }

  /**
   * Extends user session properties directly out of the validated JWT claims payload
   */
  static async getUserInfo() {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      if (!idToken) {
        throw new Error('No ID token available');
      }
      return {
        username: idToken.payload['cognito:username'] as string,
        email: idToken.payload.email as string,
        userId: idToken.payload.sub as string,
        groups: idToken.payload['cognito:groups'] as string[] | undefined
      };
    } catch (error) {
      console.error('Error getting user info:', error);
      throw error;
    }
  }

  /**
   * Synchronously checks if the browser currently stores a valid, unexpired ID Token
   */
  static async isAuthenticated(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      return !!session.tokens?.idToken;
    } catch (error) {
      return false;
    }
  }

  /**
   * Securely purges all stored Cognito JWT keys from the browser's local storage
   */
  static async signOut() {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  /**
   * Step 1: Request password reset verification code
   */
  static async requestPasswordReset(username: string) {
    try {
      return await resetPassword({ username });
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw error;
    }
  }

  /**
   * Step 2: Submit the confirmation code along with the new chosen password
   */
  static async submitNewPassword(username: string, code: string, newPassword: string) {
    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword: newPassword
      });
    } catch (error) {
      console.error('Error confirming new password:', error);
      throw error;
    }
  }
}

export default AuthService;